export type DialCode = { iso2: string; dialCode: string };

export const PHONE_PREFIX_NOT_ALLOWED = 'phone_prefix_not_allowed';

export type PhonePrefixPolicy = {
    parsePrefixes(raw: string | undefined): string[];
    isAllowedPhonePrefix(phone: string, allowed: string[]): boolean;
    isAllowedPhonePrefixWithExtras(phone: string, allowed: string[], extras: string[]): boolean;
    extractPhonePrefix(phone: string | null | undefined, catalog: DialCode[]): string | null;
};

/** Split a CSV of E.164 prefixes. Empty or missing input is an empty list (fail-open). */
export const parsePrefixes = (raw: string | undefined): string[] => {
    if (!raw) {
        return [];
    }

    return raw
        .split(',')
        .map((prefix) => prefix.trim())
        .filter(Boolean);
};

/** Normalize the JSON-array or comma-separated configuration shared by pickers and mutations. */
export const normalizeAllowedPhonePrefixes = (prefixes?: string | readonly string[]): string[] =>
    (typeof prefixes === 'string' ? parsePrefixes(prefixes) : (prefixes ?? [])).map(canonicalizePrefix).filter(Boolean);

const canonicalizePrefix = (prefix: string): string => {
    const trimmed = prefix.trim();
    if (!trimmed) {
        return '';
    }
    return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
};

/**
 * Trim and drop internal whitespace before a prefix match.
 * A leading space is rejected when the whitelist is active: the `+` must already be first.
 */
export const normalizePhoneForPrefixMatch = (phone: string): string => phone.trim().replace(/\s+/g, '');

export const isAllowedPhonePrefix = (phone: string, allowed: string[]): boolean => {
    if (allowed.length === 0) {
        return true;
    }

    if (/^\s/.test(phone)) {
        return false;
    }

    const normalized = normalizePhoneForPrefixMatch(phone);
    if (!normalized.startsWith('+')) {
        return false;
    }

    return allowed.some((prefix) => {
        const canonical = canonicalizePrefix(prefix);
        return canonical.length > 1 && normalized.startsWith(canonical);
    });
};

export const unionPhonePrefixes = (allowed: string[], extras: string[]): string[] => {
    const seen = new Set<string>();
    const merged: string[] = [];

    for (const prefix of [...allowed, ...extras]) {
        const canonical = canonicalizePrefix(prefix);
        if (!canonical || seen.has(canonical)) {
            continue;
        }
        seen.add(canonical);
        merged.push(canonical);
    }

    return merged;
};

export const isAllowedPhonePrefixWithExtras = (phone: string, allowed: string[], extras: string[]): boolean => {
    if (allowed.length === 0 || extras.length === 0) {
        return isAllowedPhonePrefix(phone, allowed);
    }

    return isAllowedPhonePrefix(phone, unionPhonePrefixes(allowed, extras));
};

/**
 * Longest catalog dial code that prefixes the number. `+1` does not win over `+1242`.
 * Returns null when the value is missing or does not start with `+`.
 */
export const extractPhonePrefix = (phone: string | null | undefined, catalog: DialCode[]): string | null => {
    if (!phone) {
        return null;
    }

    const compact = phone.replace(/\s+/g, '');
    if (!compact.startsWith('+')) {
        return null;
    }

    const digits = compact.slice(1);
    let best: string | null = null;

    for (const entry of catalog) {
        const code = entry.dialCode.replace(/^\+/, '').replace(/\s+/g, '');
        if (!code || !digits.startsWith(code)) {
            continue;
        }
        if (best === null || code.length > best.length) {
            best = code;
        }
    }

    return best ? `+${best}` : null;
};

/** Same policy used by registration and profile mutations. `extras` is only the session prefix. */
export const assertAllowedPhone = (phone: string, allowed: string[], extras: string[] = []): boolean =>
    extras.length === 0 ? isAllowedPhonePrefix(phone, allowed) : isAllowedPhonePrefixWithExtras(phone, allowed, extras);

export const phonePrefixPolicy: PhonePrefixPolicy = {
    parsePrefixes,
    isAllowedPhonePrefix,
    isAllowedPhonePrefixWithExtras,
    extractPhonePrefix,
};
